import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/utils/tailwind';

import { loadBrightcoveScript } from './loadBrightcoveScript';
import {
  brightcovePlayerScriptUrl,
  parseBrightcovePlayerUrl,
} from './parseBrightcovePlayerUrl';

type BrightcoveWindow = Window & {
  bc?: (element: Element) => BrightcovePlayerInstance | undefined;
  videojs?: {
    getPlayer?: (id: string) => BrightcovePlayerInstance | undefined;
  };
};

interface BrightcovePlayerInstance {
  dispose: () => void;
  isDisposed?: () => boolean;
}

export interface BrightcovePlayerProps {
  /** Brightcove player page URL (`players.brightcove.net/.../index.html?videoId=`). */
  src: string;
  title?: string;
  className?: string;
}

const scriptIdFor = (accountId: string, playerId: string, embed: string) =>
  `brightcove-player-${accountId}-${playerId}-${embed}`;

const disposePlayer = (videoEl: HTMLElement) => {
  const { videojs } = window as BrightcoveWindow;
  const player =
    videoEl.id && videojs?.getPlayer
      ? videojs.getPlayer(videoEl.id)
      : undefined;

  if (player && !player.isDisposed?.()) {
    player.dispose();
  }
};

/**
 * In-page Brightcove embed so media events (`timeupdate`, etc.) fire in the
 * parent document (needed for heartbeat idle detection). Falls back to an
 * iframe if the player URL cannot be parsed or the player script fails to load.
 */
export const BrightcovePlayer = ({
  src,
  title,
  className,
}: BrightcovePlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const ids = parseBrightcovePlayerUrl(src);
  // Tied to `src` so a URL change clears a prior script-load failure fallback.
  const [iframeFallbackSrc, setIframeFallbackSrc] = useState<string | null>(
    null,
  );
  const forceIframe = iframeFallbackSrc === src;
  const useIframe = !ids || forceIframe;

  useEffect(() => {
    if (useIframe) {
      return;
    }

    const parsed = parseBrightcovePlayerUrl(src);
    if (!parsed || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const videoEl = document.createElement('video-js');
    const elementId = `bc-${reactId.replaceAll(':', '')}`;
    videoEl.id = elementId;
    videoEl.setAttribute('data-account', parsed.accountId);
    videoEl.setAttribute('data-player', parsed.playerId);
    videoEl.setAttribute('data-embed', parsed.embed);
    videoEl.setAttribute('data-video-id', parsed.videoId);
    videoEl.setAttribute('controls', '');
    videoEl.classList.add('vjs-fluid');
    if (title) {
      videoEl.setAttribute('title', title);
    }

    const scriptUrl = brightcovePlayerScriptUrl(parsed);
    const scriptId = scriptIdFor(
      parsed.accountId,
      parsed.playerId,
      parsed.embed,
    );
    let cancelled = false;

    loadBrightcoveScript(scriptUrl, scriptId)
      .then(() => {
        if (cancelled) {
          return;
        }
        const { bc, videojs } = window as BrightcoveWindow;
        // Attach and initialize in one synchronous block. A player script for a
        // different account auto-initializes any video-js node already in the
        // document, which requests this video under that account (404
        // VIDEO_NOT_FOUND) and leaves the embed on PLAYER_ERR_NO_SRC.
        container.replaceChildren(videoEl);
        const alreadyInitialized =
          videoEl.id && videojs?.getPlayer
            ? videojs.getPlayer(videoEl.id)
            : undefined;
        if (alreadyInitialized) {
          return;
        }
        if (typeof bc === 'function') {
          bc(videoEl);
        }
      })
      .catch((error: unknown) => {
        console.error('[BrightcovePlayer]', error);
        if (!cancelled) {
          setIframeFallbackSrc(src);
        }
      });

    return () => {
      cancelled = true;
      disposePlayer(videoEl);
      videoEl.remove();
    };
  }, [src, reactId, title, useIframe]);

  if (useIframe) {
    return (
      <iframe
        allow="fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
        className={cn('aspect-video w-full border-0', className)}
        data-brightcove-src={src}
        data-testid="BrightcovePlayer"
        src={src}
        title={title ?? 'Video'}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('aspect-video w-full', className)}
      data-brightcove-src={src}
      data-testid="BrightcovePlayer"
      title={title}
    />
  );
};
